import React from "react";

/**
 * KpiFormFields
 * ─────────────────────────────────────────────────────────────
 * Renders dynamic form fields driven by the kpi_form_schema API.
 *
 * Props:
 *   schema      – array of field objects from GET /quantative-data/kpi-form-schema/:kpi
 *   kpiType     – e.g. "NOx", "SOx", "PPM"
 *   formData    – current values: { [fieldKey]: value }
 *                 For fields with supportedUnits, value is { value, unit }
 *                 For other fields, value is a scalar
 *   onChange    – (fieldKey, value) => void
 *   readOnly    – if true, all inputs are disabled
 *   variant     – "modal" (default) | "accordion"
 *   unitMaster  – [{ id, unit, unit_name }] fetched from getUnitMaster API
 *                 Used to resolve supportedUnits IDs → unit strings
 */
const KpiFormFields = ({
  schema,
  kpiType,
  formData = {},
  onChange,
  readOnly = false,
  variant = "modal",
  unitMaster = [],
}) => {
  if (!schema || schema.length === 0) return null;

  const inputClass = variant === "accordion" ? "ahp-input" : "kpi-input";
  const selectClass =
    variant === "accordion" ? "ahp-sel ahp-input" : "kpi-input";

  // Group fields by row number
  const rowMap = {};
  schema.forEach((field) => {
    const row = field.row ?? 1;
    if (!rowMap[row]) rowMap[row] = [];
    rowMap[row].push(field);
  });
  const sortedRows = Object.keys(rowMap).sort(
    (a, b) => Number(a) - Number(b)
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {sortedRows.map((rowKey) => {
        const fields = rowMap[rowKey];
        return (
          <div
            key={rowKey}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${fields.length}, 1fr)`,
              gap: 10,
            }}
          >
            {fields.map((field) => (
              <div key={field.key}>
                <FieldLabel required={!readOnly && field.required} variant={variant}>
                  {field.field}
                </FieldLabel>
                <FieldInput
                  field={field}
                  value={formData[field.key] ?? ""}
                  onChange={onChange}
                  readOnly={readOnly}
                  inputClass={inputClass}
                  selectClass={selectClass}
                  unitMaster={unitMaster}
                  variant={variant}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

/* ── Label ── */
const FieldLabel = ({ children, required, variant }) => {
  const isAccordion = variant === "accordion";
  return isAccordion ? (
    <label className="ahp-lbl" style={{ marginBottom: 5 }}>
      {children}
      {required && <span style={{ color: "var(--red)", marginLeft: 2 }}>*</span>}
    </label>
  ) : (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "var(--ink-3)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontFamily: "var(--font-mono)",
        marginBottom: 6,
      }}
    >
      {children}
      {required && <span style={{ color: "var(--red)", marginLeft: 2 }}>*</span>}
    </div>
  );
};

/* ── Input renderer ── */
const FieldInput = ({ field, value, onChange, readOnly, inputClass, selectClass, unitMaster, variant }) => {
  const placeholder = field.placeholder || `Enter ${field.field.toLowerCase()}`;
  const hasUnits =
    Array.isArray(field.supportedUnits) && field.supportedUnits.length > 0;

  // ── Unit-bearing field ─────────────────────────────────────────────────────
  if (hasUnits) {
    // Resolve unit IDs → { id, unit, unit_name } objects
    const unitOptions = field.supportedUnits
      .map((su) => unitMaster.find((u) => String(su.unit_id) === String(u.id)))
      .filter(Boolean);

    // value may be { value, unit } or a plain scalar (backward-compat)
    const currentVal =
      value && typeof value === "object" && "value" in value
        ? (value.value ?? "")
        : (value ?? "");
    const currentUnit =
      value && typeof value === "object" && "unitId" in value
        ? unitMaster.find((u) => String(u.id) === String(value.unitId))
        : unitMaster.find((u) => String(u.id) === String(unitOptions.find( u => u.is_base )?.id));

    const update = (newVal, newUnitId) =>
      onChange(field.key, { value: newVal, unitId: newUnitId });

    const isAccordion = variant === "accordion";
    const unitBadgeStyle = {
      padding: "0 10px",
      display: "flex",
      alignItems: "center",
      fontSize: 11,
      fontWeight: 700,
      color: isAccordion ? "var(--ink3)" : "var(--ink-3)",
      background: isAccordion ? "var(--bdr2)" : "var(--surface-2)",
      border: isAccordion ? "1px solid var(--bdr)" : "1px solid var(--rule)",
      borderRadius: 7,
      whiteSpace: "nowrap",
      height: 34,
      flexShrink: 0,
    };

    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="number"
          className={inputClass}
          placeholder={placeholder}
          value={currentVal}
          onChange={
            readOnly
              ? undefined
              : (e) => update(e.target.value, currentUnit.id)
          }
          disabled={readOnly}
          style={{ flex: 1, minWidth: 0 }}
        />
        {readOnly ? (
          <span style={unitBadgeStyle}>{currentUnit?.unit || "—"}</span>
        ) : (
          <select
            className={selectClass}
            value={currentUnit.id}
            onChange={(e) => update(currentVal, e.target.value)}
            disabled={readOnly}
            style={{ width: 90, flexShrink: 0 }}
          >
            {unitOptions.length === 0 && <option value="">—</option>}
            {unitOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unit}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  }

  // ── Regular (non-unit) fields ──────────────────────────────────────────────
  switch (field.type) {
    case "number":
      return (
        <input
          type="number"
          className={inputClass}
          placeholder={placeholder}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange(field.key, e.target.value)}
          disabled={readOnly}
          style={{ width: "100%" }}
        />
      );

    case "text":
      return (
        <input
          type="text"
          className={inputClass}
          placeholder={placeholder}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange(field.key, e.target.value)}
          disabled={readOnly}
          style={{ width: "100%" }}
        />
      );

    case "dropdown":
      return (
        <select
          className={selectClass}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange(field.key, e.target.value)}
          disabled={readOnly}
          style={{ width: "100%" }}
        >
          <option value="">Select…</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );

    case "boolean":
      return (
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { id: "true", label: "Yes" },
            { id: "false", label: "No" },
          ].map((opt) => {
            const active = String(value) === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={readOnly ? undefined : () => onChange(field.key, opt.id)}
                disabled={readOnly}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: 8,
                  border: `2px solid ${active ? "var(--accent)" : "var(--rule)"}`,
                  background: active ? "var(--accent-3)" : "var(--surface)",
                  color: active ? "var(--accent)" : "var(--ink-3)",
                  fontWeight: active ? 700 : 500,
                  fontSize: 12,
                  cursor: readOnly ? "default" : "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.15s",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      );

    case "date":
      return (
        <input
          type="date"
          className={inputClass}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange(field.key, e.target.value)}
          disabled={readOnly}
          style={{ width: "100%" }}
        />
      );

    default:
      return (
        <input
          type="text"
          className={inputClass}
          placeholder={placeholder}
          value={value}
          onChange={readOnly ? undefined : (e) => onChange(field.key, e.target.value)}
          disabled={readOnly}
          style={{ width: "100%" }}
        />
      );
  }
};

export default KpiFormFields;
